import { Link as RouterLink, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tipsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { useUser } from '../../lib/firebase/use-user.js';
import { Avatar } from '../../components/shell/Avatar.js';
import { Markdown } from '../../components/shared/Markdown.js';
import { Spinner } from '../../components/shared/Spinner.js';
import { CommentList } from '../../components/comments/CommentList.js';
import { CommentComposer } from '../../components/comments/CommentComposer.js';
import { FavoriteButton } from '../../components/shared/FavoriteButton.js';
import { EditOwnButton } from '../../components/me/EditOwnButton.js';
import { ProjectBadge } from '../../components/shared/ProjectBadge.js';
import { ForbiddenPage } from '../ForbiddenPage.js';
import { timeAgo } from '../../lib/utils/time.js';

export function TipDetailPage() {
  const { id = '' } = useParams();
  const { profile } = useAuth();
  const q = useQuery({
    queryKey: ['tip', id],
    queryFn: () => tipsDb.findById(id),
    enabled: !!id,
  });
  const author = useUser(q.data?.createdBy);

  if (q.isPending) {
    return (
      <div className="page">
        <Spinner />
      </div>
    );
  }
  if (q.error || !q.data) return <ForbiddenPage />;

  const t = q.data;
  const isOwner = profile?.id === t.createdBy;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
        <RouterLink to="/tips" className="btn ghost sm">
          ← TIPS に戻る
        </RouterLink>
        {isOwner && <EditOwnButton kind="tip" id={t.id} size="sm" />}
      </div>

      <div className="detail-layout">
        <div>
          <div className="detail-eyebrow">TIP · #{id.toUpperCase()}</div>
          <h1 className="detail-title">{t.title}</h1>
          <div className="detail-meta">
            {author.data && <Avatar user={author.data} size="sm" />}
            {author.data && <span>{author.data.name}</span>}
            <span className="dot-sep">·</span>
            <span>{timeAgo(t.createdAt)}</span>
          </div>

          <div style={{ marginTop: 18 }}>
            <Markdown>{t.body}</Markdown>
          </div>

          <div style={{ marginTop: 26 }}>
            <div className="section-title">コメント</div>
            <CommentComposer
              targetType="tip"
              targetId={t.id}
              targetVisibility={t.visibility}
            />
            <CommentList targetType="tip" targetId={t.id} parentVisibility={t.visibility} />
          </div>
        </div>

        <aside className="detail-aside">
          <div className="aside-section">
            <div className="aside-label">作成者</div>
            <div className="aside-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {author.data && <Avatar user={author.data} size="sm" />}
              {author.data?.name ?? '—'}
            </div>
          </div>
          <div className="aside-section">
            <div className="aside-label">タグ</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {t.tags.map((tag) => (
                <span key={tag} className="tag"><span className="tag-dot" />{tag}</span>
              ))}
            </div>
          </div>
          <div className="aside-section">
            <div className="aside-label">作成日時</div>
            <div className="aside-value mono" style={{ fontSize: 12 }}>
              {t.createdAt.toLocaleString('ja-JP')}
            </div>
          </div>
          {t.projectId && (
            <div className="aside-section">
              <div className="aside-label">トピック</div>
              <div className="aside-value">
                <ProjectBadge projectId={t.projectId} />
              </div>
            </div>
          )}
          <div className="aside-section">
            <FavoriteButton targetType="tip" targetId={t.id} fullWidth />
          </div>
        </aside>
      </div>
    </div>
  );
}
